import axios from "axios";

const LAMBDA_PROXY_BASE = "https://0nc70p8y1i.execute-api.eu-central-1.amazonaws.com/";

// Cache configuration for TripAdvisor responses
const TRIPADVISOR_CACHE_KEY = "tripAdvisorCache";
const CACHE_EXPIRY_HOURS = 24; // Cache expires after 24 hours
const MAX_CACHE_SIZE = 500; // Maximum number of cached entries

// Cache utilities
const getCacheKey = (data) => {
  const { hotelId, hotelName, cityName, countryName } = data;
  return `${hotelId}_${hotelName}_${cityName}_${countryName}`.toLowerCase().replace(/[^a-z0-9_]/g, '');
};

const loadCache = () => {
  try {
    const cached = localStorage.getItem(TRIPADVISOR_CACHE_KEY);
    if (!cached) return {};
    
    const parsedCache = JSON.parse(cached);
    const now = Date.now();
    
    // Clean expired entries
    const cleanedCache = {};
    Object.entries(parsedCache).forEach(([key, entry]) => {
      if (entry.timestamp && (now - entry.timestamp) < (CACHE_EXPIRY_HOURS * 60 * 60 * 1000)) {
        cleanedCache[key] = entry;
      }
    });
    
    // If cache was cleaned, save it back
    if (Object.keys(cleanedCache).length !== Object.keys(parsedCache).length) {
      saveCache(cleanedCache);
    }
    
    return cleanedCache;
  } catch (error) {
    console.error("Error loading TripAdvisor cache:", error);
    return {};
  }
};

const saveCache = (cache) => {
  try {
    // Limit cache size by removing oldest entries
    const entries = Object.entries(cache);
    if (entries.length > MAX_CACHE_SIZE) {
      entries.sort((a, b) => (b[1].timestamp || 0) - (a[1].timestamp || 0));
      const limitedCache = {};
      entries.slice(0, MAX_CACHE_SIZE).forEach(([key, value]) => {
        limitedCache[key] = value;
      });
      localStorage.setItem(TRIPADVISOR_CACHE_KEY, JSON.stringify(limitedCache));
    } else {
      localStorage.setItem(TRIPADVISOR_CACHE_KEY, JSON.stringify(cache));
    }
  } catch (error) {
    console.error("Error saving TripAdvisor cache:", error);
    // If localStorage is full, clear the cache and try again
    try {
      localStorage.removeItem(TRIPADVISOR_CACHE_KEY);
      localStorage.setItem(TRIPADVISOR_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.error("Failed to save TripAdvisor cache even after clearing:", e);
    }
  }
};

export const fetchTripadvisorMatch = async (data) => {
  try {
    const {
      hotelId,
      hotelName,
      cityName,
      countryName,
      latitude,
      longitude,
      address,
    } = data;

    // Generate cache key
    const cacheKey = getCacheKey(data);
    
    // Check cache first
    const cache = loadCache();
    if (cache[cacheKey]) {
      return cache[cacheKey].data;
    }

    const payload = {
      hotelId,
      hotelName,
      cityName,
      countryName,
      latitude, 
      longitude,
      address,
    };


    const res = await axios.post(
      `${LAMBDA_PROXY_BASE}/tripadvisor/match`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000, // 10 second timeout
      }
    );
    
    const responseData = res.data || null;
    
    // Cache the response
    if (responseData) {
      cache[cacheKey] = {
        data: responseData,
        timestamp: Date.now()
      };
      saveCache(cache);
    }
    
    return responseData;
  } catch (err) {
    console.error("TripAdvisor proxy error:", err.message || err);
    return null;
  }
};

// Utility function to clear TripAdvisor cache (for debugging or maintenance)
export const clearTripAdvisorCache = () => {
  try {
    localStorage.removeItem(TRIPADVISOR_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing TripAdvisor cache:", error);
  }
};

// Utility function to get cache statistics
export const getTripAdvisorCacheStats = () => {
  try {
    const cache = loadCache();
    return {
      totalEntries: Object.keys(cache).length,
      cacheSize: localStorage.getItem(TRIPADVISOR_CACHE_KEY)?.length || 0,
      oldestEntry: Math.min(...Object.values(cache).map(entry => entry.timestamp || Date.now())),
      newestEntry: Math.max(...Object.values(cache).map(entry => entry.timestamp || Date.now()))
    };
  } catch (error) {
    console.error("Error getting TripAdvisor cache stats:", error);
    return { totalEntries: 0, cacheSize: 0, oldestEntry: null, newestEntry: null };
  }
};
